import { graphql } from 'react-relay';
import { useRef, useCallback, useState, useContext } from 'react';
import { NetworkStatus } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { isApolloError } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { SPACE_DIRECTORY_PAGINATE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';

import type {
	useSpaceDirectoryDataSpaceDirectorySpacesQuery_spaces_PaginatedSpaceList_pageInfo_PageInfo as PageInfo,
	useSpaceDirectoryDataSpaceDirectorySpacesQuery_spaces_PaginatedSpaceList_nodes_Space as Space,
	useSpaceDirectoryDataSpaceDirectorySpacesQuery as useSpaceDirectoryDataSpaceDirectorySpacesQuery$data,
	useSpaceDirectoryDataSpaceDirectorySpacesQueryVariables as useSpaceDirectoryDataSpaceDirectorySpacesQuery$variables,
} from './__types__/useSpaceDirectoryDataSpaceDirectorySpacesQuery.ts';
import type { Filters } from './types.ts';
import { PillFilter } from './Filters/PillFilters.tsx';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const PAGE_SIZE = 25;

export enum LoadingState {
	done,
	initial,
	pagination,
}

const getLoadingState = (networkStatus: NetworkStatus) => {
	if (networkStatus === NetworkStatus.setVariables) {
		return LoadingState.initial;
	}
	if (networkStatus === NetworkStatus.fetchMore || networkStatus === NetworkStatus.loading) {
		return LoadingState.pagination;
	}
	return LoadingState.done;
};

const normalizeQueryData = (
	data: useSpaceDirectoryDataSpaceDirectorySpacesQuery$data | undefined,
) => {
	let unfilteredSpaces: (Space | null | undefined)[] | null | undefined;
	let pageInfo: PageInfo = { hasNextPage: false, endCursor: null };
	if (data !== undefined && data?.spaces) {
		unfilteredSpaces = data.spaces?.nodes;
		pageInfo = data.spaces?.pageInfo ?? pageInfo;
	}

	const spaces = (unfilteredSpaces ?? []).filter(Boolean) as Space[];

	return {
		spaces,
		pageInfo,
	};
};

function isWatchedError(error: GraphQLError): boolean;
function isWatchedError(error: ApolloErrorInterop): boolean;
function isWatchedError(error: GraphQLError | ApolloErrorInterop): boolean {
	const predicate = ({ path }: GraphQLError) =>
		!!path && path[0] === 'spaces' && path[4] === 'isWatched';
	return isApolloError(error)
		? error.graphQLErrors.every((err) => predicate(err))
		: predicate(error);
}

export const useSpaceDirectoryData = ({ filters }: { filters: Filters }) => {
	const page = useRef(0);
	const { isLicensed } = useSessionData();
	const { isProductAdmin } = useIsProductAdmin();
	const [nextPageError, setNextPageError] = useState<ApolloErrorInterop>();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const getVariables = (): useSpaceDirectoryDataSpaceDirectorySpacesQuery$variables => {
		const variables: useSpaceDirectoryDataSpaceDirectorySpacesQuery$variables = {
			first: PAGE_SIZE,
			offset: 0,
			name: filters.search,
			status: 'current',
			isLicensed,
		};

		switch (filters.spaceType) {
			case PillFilter.COMMUNAL:
				variables.excludeTypes = 'system,personal';
				break;
			case PillFilter.WATCHING:
				variables.watching = true;
				break;
			case PillFilter.STARRED:
				variables.favourite = true;
				break;
			case PillFilter.PERSONAL:
				variables.type = 'personal';
				break;
			case PillFilter.ARCHIVED:
				variables.status = 'archived';
				break;
			case PillFilter.TRASHED:
				if (isProductAdmin) {
					variables.status = 'trashed';
				}
				break;
		}

		if (filters.label) {
			variables.label = [filters.label];
		}

		return variables;
	};

	// We don't need to load any spaces since we are showing a message that redirects
	// them to view trashed spaces in settings
	const skip = filters.spaceType === 'trashed';

	const {
		data,
		loading,
		error: firstPageError,
		fetchMore,
		networkStatus,
	} = useQueryInterop<
		useSpaceDirectoryDataSpaceDirectorySpacesQuery$data,
		useSpaceDirectoryDataSpaceDirectorySpacesQuery$variables
	>(
		graphql`
			query useSpaceDirectoryDataSpaceDirectorySpacesQuery(
				$name: String
				$type: String
				$excludeTypes: String
				$status: String
				$label: [String]
				$watching: Boolean
				$favourite: Boolean
				$offset: Int
				$first: Int
				$isLicensed: Boolean!
			) {
				spaces(
					spaceNamePattern: $name
					type: $type
					excludeTypes: $excludeTypes
					status: $status
					label: $label
					watchedSpacesOnly: $watching
					favourite: $favourite
					offset: $offset
					first: $first
				) {
					nodes {
						alias
						id
						name
						key
						icon {
							path
							isDefault
						}
						currentUser @include(if: $isLicensed) {
							isAdmin
							isFavourited
							isWatched
						}
						type
						status
						metadata {
							labels {
								nodes {
									id
									name
									prefix
								}
							}
						}
						description {
							plain {
								value
							}
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		`,
		{
			variables: getVariables(),
			notifyOnNetworkStatusChange: true,
			errorPolicy: 'all',
			fetchPolicy: 'network-only',
			skip,
		},
	);

	if (firstPageError && isWatchedError(firstPageError)) {
		firstPageError.extraInfo = { isWatchedError: true };
	}

	const {
		spaces,
		pageInfo: { hasNextPage, endCursor },
	} = normalizeQueryData(data);

	const nextPage = useCallback(async () => {
		if (hasNextPage && endCursor && !loading && !firstPageError && !nextPageError) {
			experienceTracker.start({
				name: SPACE_DIRECTORY_PAGINATE_EXPERIENCE,
				timeout: ExperienceTimeout.SPACE_DIRECTORY,
			});
			page.current++;
			const { errors } = await fetchMore({
				variables: {
					offset: page.current * PAGE_SIZE,
				},
				updateQuery: (prevResult, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResult;
					}

					const { spaces: prevSpaces } = normalizeQueryData(prevResult);
					const { spaces: fetchMoreSpaces, pageInfo: fetchMorePageInfo } =
						normalizeQueryData(fetchMoreResult);

					const mergedSpaces = [...prevSpaces, ...fetchMoreSpaces];

					return (
						fetchMoreResult && {
							spaces: {
								__typename: 'PaginatedSpaceList',
								nodes: mergedSpaces,
								pageInfo: fetchMorePageInfo,
							},
						}
					);
				},
			});

			if (errors) {
				setNextPageError({
					name: 'NextPageError',
					message: 'Space directory failed to fetch more data',
					graphQLErrors: errors,
					networkError: null,
					extraInfo: {
						isWatchedError: errors.every(isWatchedError),
					},
				});
			}
		}
	}, [
		hasNextPage,
		endCursor,
		fetchMore,
		loading,
		firstPageError,
		nextPageError,
		experienceTracker,
	]);

	return {
		spaces,
		loadingState: getLoadingState(networkStatus),
		firstPageError,
		nextPageError,
		hasNextPage: !!hasNextPage,
		nextPage,
		pageIndex: page.current,
	};
};
